import { Link } from 'react-router-dom';
import {
  FiHome,
  FiCreditCard,
  FiDollarSign,
  FiSettings,
  FiRefreshCw,
  FiSearch,
  FiEdit2,
  FiCalendar,
  FiHelpCircle,
  FiCheckCircle,
  FiArrowRight,
  FiZap,
  FiEye,
  FiFilter,
  FiPieChart,
  FiTrendingUp,
  FiUsers,
  FiRotateCcw
} from 'react-icons/fi';

const Help = () => {
  const sections = [
    {
      title: 'Dashboard',
      icon: FiHome,
      color: 'blue',
      path: '/dashboard',
      description: 'Your home base! See all your important numbers at a glance.',
      features: [
        { icon: FiTrendingUp, text: 'Monthly Revenue (MRR) - How much money comes in each month' },
        { icon: FiUsers, text: 'Active Subscriptions - How many customers are paying right now' },
        { icon: FiPieChart, text: 'Charts - Pretty pictures that show where your customers come from' },
        { icon: FiRotateCcw, text: 'Refunds - Money that was given back to customers' }
      ],
      tips: [
        'Click "Refresh from Stripe" to get the latest data',
        'Use Daily/Weekly/Monthly buttons to change the chart view',
        'Scroll down to see which salespeople bring the most customers'
      ]
    },
    {
      title: 'Subscriptions',
      icon: FiCreditCard,
      color: 'green',
      path: '/subscriptions',
      description: 'See all your customers and their subscriptions in one place.',
      features: [
        { icon: FiEye, text: 'View all customers - names, emails, and payment status' },
        { icon: FiFilter, text: 'Filter by status (Active, Canceled, etc.) or by source' },
        { icon: FiSearch, text: 'Search for any customer by name or email' },
        { icon: FiEdit2, text: 'Edit the source/salesperson for any subscription' }
      ],
      tips: [
        'Click on any row to see full details (payments, refunds, totals)',
        'Click the colored dot next to a source to edit it',
        'Use filters to find specific types of subscriptions quickly'
      ]
    },
    {
      title: 'Commissions',
      icon: FiDollarSign,
      color: 'emerald',
      path: '/commissions',
      description: 'Track how much each salesperson earns from their sales.',
      features: [
        { icon: FiDollarSign, text: 'Total commissions - see who earns what' },
        { icon: FiCalendar, text: 'Filter by date - This Month, Last Month, or Custom Range' },
        { icon: FiPieChart, text: 'Charts showing top performers' },
        { icon: FiRotateCcw, text: 'Refunds are automatically deducted from commissions' }
      ],
      tips: [
        'Click on any salesperson to see their individual sales',
        'Commissions are calculated at 10% of sales',
        'Salespeople are grouped by section (Closers, SDRs, CSMs)'
      ]
    },
    {
      title: 'Settings',
      icon: FiSettings,
      color: 'gray',
      path: '/settings',
      description: 'Manage your data and sync settings.',
      features: [
        { icon: FiRefreshCw, text: 'Force Refresh - Completely re-sync all data from Stripe' },
        { icon: FiCheckCircle, text: 'See when data was last synced' },
        { icon: FiZap, text: 'Auto-sync runs every 15 minutes automatically' }
      ],
      tips: [
        'Use Force Refresh if you notice missing or wrong data',
        'Your edited sources are kept safe during refresh',
        'The refresh shows a live log so you can see what\'s happening'
      ]
    }
  ];

  const quickStartSteps = [
    {
      step: 1,
      title: 'Check the Dashboard',
      description: 'Start here to see your overall business health',
      action: 'Go to Dashboard',
      path: '/dashboard'
    },
    {
      step: 2,
      title: 'Refresh Your Data',
      description: 'Click "Refresh from Stripe" to get the latest numbers',
      action: 'Go to Dashboard',
      path: '/dashboard'
    },
    {
      step: 3,
      title: 'View Subscriptions',
      description: 'See all your customers and their payment status',
      action: 'Go to Subscriptions',
      path: '/subscriptions'
    },
    {
      step: 4,
      title: 'Check Commissions',
      description: 'See how much each salesperson has earned',
      action: 'Go to Commissions',
      path: '/commissions'
    }
  ];

  const faqs = [
    {
      question: 'How do I update the source/salesperson for a subscription?',
      answer: 'Go to Subscriptions, find the customer, and click on the colored dot next to their source name. Type the new source and click Save.'
    },
    {
      question: 'Why is some data missing?',
      answer: 'Try clicking "Refresh from Stripe" on the Dashboard, or use "Force Refresh" in Settings to re-sync everything.'
    },
    {
      question: 'How often does the data update?',
      answer: 'Data syncs automatically every 15 minutes. You can also manually refresh anytime by clicking the refresh button.'
    },
    {
      question: 'Will I lose my edited sources if I do a Force Refresh?',
      answer: 'No! Your manually edited source/salesperson names are saved and restored after the refresh.'
    },
    {
      question: 'How are commissions calculated?',
      answer: 'Commissions are 10% of sales. If a customer gets a refund, that commission is deducted automatically.'
    },
    {
      question: 'What do the different subscription statuses mean?',
      answer: 'Active = paying customer, Trialing = free trial, Canceled = no longer paying, Past Due = payment failed.'
    }
  ];

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      icon: 'bg-blue-100'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-600',
      icon: 'bg-green-100'
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-600',
      icon: 'bg-emerald-100'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-600',
      icon: 'bg-gray-100'
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <FiHelpCircle className="w-8 h-8 text-primary-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Welcome to Trendmonster Portal</h1>
        <p className="mt-2 text-lg text-gray-600">
          Your simple guide to managing subscriptions and tracking commissions
        </p>
      </div>

      {/* Quick Start */}
      <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <FiZap className="text-primary-600" />
          Quick Start Guide
        </h2>
        <p className="text-gray-600 mb-6">New here? Follow these simple steps to get started:</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickStartSteps.map((item) => (
            <Link
              key={item.step}
              to={item.path}
              className="bg-white rounded-lg p-4 border border-primary-100 hover:border-primary-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white font-bold text-sm">
                  {item.step}
                </span>
                <span className="font-semibold text-gray-900 group-hover:text-primary-600">
                  {item.title}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{item.description}</p>
              <span className="text-sm text-primary-600 flex items-center gap-1">
                {item.action} <FiArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* App Sections */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Understanding Each Section
        </h2>
        <div className="space-y-4">
          {sections.map((section) => {
            const colors = colorClasses[section.color];
            return (
              <div
                key={section.title}
                className={`card ${colors.bg} ${colors.border} border`}
              >
                {/* Section Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${colors.icon}`}>
                      <section.icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
                      <p className="text-gray-600">{section.description}</p>
                    </div>
                  </div>
                  <Link
                    to={section.path}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${colors.icon} ${colors.text} text-sm font-medium hover:opacity-80 transition-opacity`}
                  >
                    Open <FiArrowRight className="w-3 h-3" />
                  </Link>
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {section.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2 bg-white bg-opacity-60 rounded-lg p-3">
                      <feature.icon className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                      <span className="text-sm text-gray-700">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* Tips */}
                <div className="bg-white bg-opacity-60 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Tips:</p>
                  <ul className="space-y-1">
                    {section.tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                        <FiCheckCircle className={`w-4 h-4 ${colors.text} mt-0.5 flex-shrink-0`} />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Common Tasks */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          How To Do Common Tasks
        </h2>

        <div className="space-y-4">
          {/* Task 1: Edit Source */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiEdit2 className="text-blue-600" />
              Change a Subscription's Source
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 ml-2">
              <li>Go to <Link to="/subscriptions" className="text-blue-600 hover:underline">Subscriptions</Link></li>
              <li>Find the customer (use the search box if needed)</li>
              <li>Click on the colored dot next to their current source</li>
              <li>Type the new source name or pick from existing ones</li>
              <li>Click "Save"</li>
            </ol>
          </div>

          {/* Task 2: Find Customer */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiSearch className="text-green-600" />
              Find a Specific Customer
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 ml-2">
              <li>Go to <Link to="/subscriptions" className="text-blue-600 hover:underline">Subscriptions</Link></li>
              <li>Type the customer's name or email in the search box</li>
              <li>Click on the customer row to see full details</li>
            </ol>
          </div>

          {/* Task 3: Check Commissions */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiDollarSign className="text-emerald-600" />
              Check a Salesperson's Commission
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 ml-2">
              <li>Go to <Link to="/commissions" className="text-blue-600 hover:underline">Commissions</Link></li>
              <li>Use the date filter to select a time period</li>
              <li>Find the salesperson in the list</li>
              <li>Click on their name to see all their sales</li>
            </ol>
          </div>

          {/* Task 4: Refresh Data */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FiRefreshCw className="text-purple-600" />
              Refresh All Data from Stripe
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 ml-2">
              <li><strong>Quick refresh:</strong> Click "Refresh from Stripe" on the <Link to="/dashboard" className="text-blue-600 hover:underline">Dashboard</Link></li>
              <li><strong>Full refresh:</strong> Go to <Link to="/settings" className="text-blue-600 hover:underline">Settings</Link> and click "Force Refresh All Data"</li>
            </ol>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
              <p className="text-sm text-gray-600">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Status Legend */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Status Colors Guide
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">active</span>
            <span className="text-sm text-gray-600">Customer is paying</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">trialing</span>
            <span className="text-sm text-gray-600">Free trial period</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">canceled</span>
            <span className="text-sm text-gray-600">No longer paying</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">past_due</span>
            <span className="text-sm text-gray-600">Payment failed</span>
          </div>
        </div>
      </div>

      {/* Need More Help */}
      <div className="text-center py-6 text-gray-500">
        <p>Still have questions? Contact your administrator for help.</p>
      </div>
    </div>
  );
};

export default Help;
